import { faDollar } from '@fortawesome/pro-regular-svg-icons';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { OfferBundleLabel, usePricingModel } from './pricing.model';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import { InputPrice } from '@design-system/components/input/inputPrice';
import { Collection } from '../collection.domain';
import { Tag } from '@design-system/components/tag/tag';
import { sprinkles } from '@design-system/sprinkles.css';
import { clsx } from 'clsx';
import { Alert } from '@design-system/components/alert/alert';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import * as styles from './ProgramPricing.css';
import { Chip } from '@design-system/components/chip/chip';
import { Link } from 'react-router';
import { PATHS } from '@client/routes';

type ProgramPricingProps = {
  creatorId: number;
  collection: Collection;
  isModified: boolean;
  save: () => Promise<boolean>;
  isOffers35Enabled: boolean;
};

const BundleItem = ({
  bundle,
  onClick,
}: {
  bundle: OfferBundleLabel;
  onClick: () => void;
}) => (
  <button type="button" className={styles.bundleLabel} onClick={onClick}>
    <Typography
      as="span"
      variant="body"
      size="medium"
      fontWeight="medium"
      color="N-100"
      className={styles.bundleName}
    >
      {bundle.name}
    </Typography>
    <Tag
      label={bundle.status.label}
      readonly
      className={clsx(
        styles.statusTag,
        sprinkles({
          background: bundle.status.color.bg,
          color: bundle.status.color.text,
        }),
      )}
    />
    <Typography
      as="span"
      variant="body"
      size="small"
      fontWeight="normal"
      color="N-80"
      className={styles.contentDescription}
    >
      {bundle.contentDescription}
    </Typography>
    <Typography
      as="span"
      variant="body"
      size="medium"
      fontWeight="medium"
      color="N-100"
      className={styles.bundlePrice}
    >
      {bundle.price}
    </Typography>
  </button>
);

export const ProgramPricing = ({
  creatorId,
  collection,
  isModified,
  save,
  isOffers35Enabled,
}: ProgramPricingProps) => {
  const model = usePricingModel({ creatorId, collection, isModified, save });

  return (
    <AsyncRenderer
      data={model}
      renderer={(data) => (
        <div className={styles.container}>
          <UnsavedChangesModal {...data.unsavedChangesModal} />
          {!isOffers35Enabled ? (
            <div className={styles.card}>
              <div className={styles.cardHeader}>
                <Typography variant="body" size="large">
                  Offers
                </Typography>
                <Link to={PATHS.BUNDLES(data.creatorId)}>
                  <Button
                    color="primary"
                    variant="text"
                    className={styles.offersButton}
                  >
                    Go to Offers
                  </Button>
                </Link>
              </div>
              {data.offers.length > 0 && (
                <>
                  <Typography
                    variant="body"
                    size="medium"
                    fontWeight="normal"
                    color="N-80"
                  >
                    This program belongs to:
                  </Typography>
                  <div className={styles.chips}>
                    {data.offers.map(({ id, name, bgColor, opacity }) => (
                      <Chip
                        key={id}
                        readonly={true}
                        label={name}
                        className={sprinkles({
                          bg: { hover: bgColor, lightMode: bgColor },
                          opacity,
                        })}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={styles.card}>
              {data.offers.length === 0 ? (
                <div className={styles.cardHeader}>
                  <div className={styles.sectionText}>
                    <Typography variant="body" size="large">
                      Offers
                    </Typography>
                    <Typography
                      variant="body"
                      size="medium"
                      fontWeight="normal"
                      color="N-80"
                    >
                      Add to Offer Adjust your landing page, price and more
                      inside of an offer
                    </Typography>
                  </div>
                  <Button
                    color="primary"
                    variant="text"
                    className={styles.offersButton}
                    onClick={data.addToOffer}
                  >
                    Add to Offer
                  </Button>
                </div>
              ) : (
                <>
                  <div className={styles.sectionText}>
                    <Typography variant="body" size="large">
                      This program is included in an offer already
                    </Typography>
                    <Typography
                      variant="body"
                      size="medium"
                      fontWeight="normal"
                      color="N-80"
                    >
                      Click the offer below and modify the price, landing page,
                      and more.
                    </Typography>
                  </div>
                  <div className={styles.bundleList}>
                    {data.offers.map((offer, index) => (
                      <BundleItem
                        key={`${offer.id}-${index}`}
                        bundle={offer}
                        onClick={() => data.openOffer(offer.id)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {data.currentPrice !== null && (
            <div className={styles.card}>
              {!isOffers35Enabled && (
                <Typography variant="body" size="medium" fontWeight="normal">
                  To change its price you need to create a new OTP Offer
                </Typography>
              )}
              <InputPrice
                label="Price"
                required
                disabled
                value={data.currentPrice / 100}
                onChange={() => undefined}
                startIcon={{ icon: faDollar }}
                wrapperClassName="lg:w-[150px]"
              />
              {isOffers35Enabled && (
                <Alert
                  variant="info"
                  customComponent={
                    <Typography
                      variant="body"
                      size="medium"
                      fontWeight="normal"
                    >
                      To change its price you need to create a new OTP Offer.
                      Read this{' '}
                      <a
                        href="https://help.playbookapp.io/en/articles/9655526-one-time-purchase-strategy-pricing-and-packaging"
                        target="_blank"
                        rel="noreferrer"
                        className={styles.helpArticleLink}
                      >
                        help article
                      </a>{' '}
                      on how to change price.
                    </Typography>
                  }
                />
              )}
            </div>
          )}
        </div>
      )}
    />
  );
};
